// Admin > Safety (Wave 1, docs/product/spec-safety.md): what the safety checks blocked or flagged, open first and
// critical on top: blocked uploads and prompts, quiet flags (a possible minor, a check that didn't run, a user who
// insisted after a block) and child-safety locks. Resolve each one (Dismiss / Actioned / Reported to NCMEC), lift a
// lock after review (Unlock, with a note), and read the real-person consent log. Admins only.
// No picture is ever shown here: critical files are preserved and hidden, and nothing else needs one.
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { Empty, ErrorNote, Sheet, Spinner, timeAgo, useLoad } from "../components/bits";
import { refreshSafetyCount, setSafetyCount } from "../components/SafetyBadge";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { canUnlock, categoriesText, filterSafety, isOpen, KIND_LABEL, KINDS, RESOLVE_LABEL, sortSafety, STATUS_LABEL, type SafetyEvent, type SafetyKind, type SafetyResolve, type SafetySeverity } from "../safety";
import { toast, useStore } from "../store";

const SEVERITY_LABEL: Record<SafetySeverity, string> = { critical: "Critical", warn: "Warning", info: "Info" };
const SOURCE_LABEL: Record<string, string> = { upload: "Upload", prompt: "Prompt", director: "Director chat", brief: "Brief" };
const fullDate = (iso: string) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function AdminSafetyScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const [status, setStatus] = useState<"open" | "all">("open");
  const [kind, setKind] = useState<"" | SafetyKind>("");
  const [unlock, setUnlock] = useState<SafetyEvent | null>(null);
  const { data, setData, error, loading, reload } = useLoad(() => (admin ? api.adminSafety({ status, kind }) : Promise.resolve(null)), [admin, status, kind]);

  // the rail badge follows what this screen just read
  useEffect(() => {
    if (data) setSafetyCount({ open: data.counts.open, critical: data.counts.critical });
  }, [data]);

  if (!admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            This page holds what the safety checks blocked or flagged.
          </Empty>
        </div>
      </Shell>
    );

  const events = data ? sortSafety(filterSafety(data.events, { status, kind })) : [];
  const update = (next: SafetyEvent) => {
    if (!data) return;
    setData({ ...data, events: data.events.map((e) => (e.id === next.id ? next : e)) });
    void refreshSafetyCount();
  };
  const unlocked = (userId: string) => {
    if (!data) return;
    setData({ ...data, events: data.events.map((e) => (e.userId === userId ? { ...e, userLocked: false } : e)) });
  };
  const c = data?.counts;

  return (
    <Shell>
      <div className="page safety">
        <header className="page-head">
          <div>
            <h1 className="page-title">Safety</h1>
            <p className="page-lede">What the safety checks blocked or flagged. Critical ones were emailed to you straight away; the rest come in an hourly digest. Users never see these.</p>
          </div>
          <span className="seg" role="radiogroup" aria-label="Show">
            <button type="button" role="radio" aria-checked={status === "open"} className={status === "open" ? "is-on" : ""} onClick={() => setStatus("open")}>
              Open
            </button>
            <button type="button" role="radio" aria-checked={status === "all"} className={status === "all" ? "is-on" : ""} onClick={() => setStatus("all")}>
              All
            </button>
          </span>
        </header>

        {c && (
          <p className="safety-counts muted">
            {c.open} open
            {c.critical > 0 && (
              <>
                {" · "}
                <strong className="safety-crit">{c.critical} critical</strong>
              </>
            )}
            {c.ncmec > 0 && ` · ${c.ncmec} waiting on the NCMEC review`}
          </p>
        )}

        <div className="fb-filters" role="tablist" aria-label="Kind">
          {(["", ...KINDS] as ("" | SafetyKind)[]).map((k) => (
            <button key={k || "all"} type="button" role="tab" aria-selected={kind === k} className={`chip${kind === k ? " is-on" : ""}`} onClick={() => setKind(k)}>
              {k ? KIND_LABEL[k] : "Every kind"}
            </button>
          ))}
        </div>

        {error && <ErrorNote text={`Couldn't load the safety queue. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading the safety queue" />}
        {data && !events.length && <Empty title={status === "open" ? "Nothing to review" : "Nothing here yet"}>{status === "open" ? "Every event has been handled." : "Blocks and flags land here."}</Empty>}
        {!!events.length && (
          <ul className="report-list safety-list">
            {events.map((e) => (
              <SafetyRow key={e.id} e={e} onDone={update} onUnlock={() => setUnlock(e)} />
            ))}
          </ul>
        )}

        {data && (
          <section className="safety-consents" aria-labelledby="consents-h">
            <h2 id="consents-h" className="group-title">
              Consent log
            </h2>
            <p className="muted">Each time someone confirmed a real-person picture is them or that they have permission.</p>
            {data.consents.length ? (
              <ul className="safety-consent-list">
                {data.consents.map((x) => (
                  <li key={x.id}>
                    <span className="safety-consent-who">{x.userEmail ?? x.userId ?? "Someone"}</span>
                    <span className="safety-consent-file">{x.fileName}</span>
                    <time className="muted" dateTime={x.createdAt} title={fullDate(x.createdAt)}>
                      {timeAgo(x.createdAt)}
                    </time>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No consents yet.</p>
            )}
          </section>
        )}
      </div>
      {unlock && (
        <UnlockSheet
          e={unlock}
          onClose={() => setUnlock(null)}
          onDone={() => {
            unlocked(unlock.userId!);
            setUnlock(null);
            void refreshSafetyCount();
          }}
        />
      )}
    </Shell>
  );
}

function SafetyRow({ e, onDone, onUnlock }: { e: SafetyEvent; onDone: (e: SafetyEvent) => void; onUnlock: () => void }) {
  const [busy, setBusy] = useState<SafetyResolve | null>(null);
  const [note, setNote] = useState("");
  const open = isOpen(e);
  const critical = e.severity === "critical";
  const resolve = async (status: SafetyResolve) => {
    setBusy(status);
    try {
      const n = await api.resolveSafety(e.id, { status, note: note.trim() || null });
      onDone(n ?? { ...e, status, note: note.trim() || e.note, resolvedAt: new Date().toISOString() });
      toast(RESOLVE_LABEL[status], "good");
    } catch (err) {
      toast(`Couldn't do that. ${(err as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };
  const meta = [e.source ? (SOURCE_LABEL[e.source] ?? e.source) : null, e.categories.length ? categoriesText(e.categories) : null].filter(Boolean).join(" · ");
  return (
    <li className={`report-card safety-card is-${e.severity}${open ? "" : " is-done"}`}>
      <header className="report-head">
        <span className={`tag safety-sev is-${e.severity}`}>{SEVERITY_LABEL[e.severity]}</span>
        <strong>{KIND_LABEL[e.kind]}</strong>
        <time className="muted" dateTime={e.createdAt} title={fullDate(e.createdAt)}>
          {timeAgo(e.createdAt)}
        </time>
        <span className={`tag${open ? " tag-warn" : ""}`}>{STATUS_LABEL[e.status]}</span>
      </header>
      <p className="report-what">
        {e.userEmail ?? e.userId ?? "Unknown user"}
        {e.kind === "csam_lock" && e.userLocked === true && <span className="muted"> · locked</span>}
        {e.kind === "csam_lock" && e.userLocked === false && <span className="muted"> · unlocked</span>}
      </p>
      {meta && <p className="muted safety-meta">{meta}</p>}
      {e.excerpt && <blockquote className="report-quote safety-excerpt">{e.excerpt}</blockquote>}
      {critical && e.assetId && <p className="muted safety-meta">The file is kept and hidden from the user. It's never shown here.</p>}
      {e.note && <p className="report-details">Note: {e.note}</p>}
      {!open && e.resolvedAt && (
        <p className="muted safety-meta">
          {STATUS_LABEL[e.status]} {timeAgo(e.resolvedAt)}
          {e.resolvedBy ? ` by ${e.resolvedBy}` : ""}
        </p>
      )}
      {(open || canUnlock(e)) && (
        <div className="report-acts">
          {open && (
            <>
              <input className="safety-note-input" type="text" value={note} onChange={(x) => setNote(x.target.value)} maxLength={500} placeholder="Note (optional)" aria-label="Note" autoComplete="off" />
              <button type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void resolve("dismissed")}>
                {busy === "dismissed" ? "Saving" : "Dismiss"}
              </button>
              <button type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void resolve("actioned")}>
                {busy === "actioned" ? "Saving" : "Actioned"}
              </button>
              <button type="button" className={`btn btn-small ${critical ? "btn-primary" : "btn-quiet"}`} disabled={!!busy} onClick={() => void resolve("reported")}>
                {busy === "reported" ? "Saving" : "Reported to NCMEC"}
              </button>
            </>
          )}
          {canUnlock(e) && (
            <button type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={onUnlock}>
              Unlock
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function UnlockSheet({ e, onClose, onDone }: { e: SafetyEvent; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (x: FormEvent) => {
    x.preventDefault();
    if (!note.trim() || busy) return;
    setBusy(true);
    try {
      await api.unlockSafety(e.userId!, note.trim());
      toast(`${e.userEmail ?? "The account"} is unlocked.`, "good");
      onDone();
    } catch (err) {
      toast(`Couldn't unlock it. ${(err as Error).message}`, "bad");
      setBusy(false);
    }
  };
  return (
    <Sheet title="Unlock this account?" onClose={onClose}>
      <form className="sheet-body confirm" onSubmit={submit}>
        <p className="ppl-who">
          <strong>{e.userEmail ?? e.userId}</strong>
        </p>
        <p>Only after you've reviewed it. They get back into the studio straight away. The preserved file stays kept and hidden.</p>
        <label className="field">
          <span className="field-label">Why (required)</span>
          <textarea value={note} onChange={(x) => setNote(x.target.value)} maxLength={500} rows={3} required placeholder="What the review found" />
          <span className="field-hint">Only admins see it.</span>
        </label>
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Keep it locked
          </button>
          <button type="submit" className="btn btn-primary" disabled={!note.trim() || busy}>
            {busy ? "Unlocking" : "Unlock"}
          </button>
        </div>
      </form>
    </Sheet>
  );
}
